/**
 * Run Workflow Dialog Component
 *
 * Dialog for executing a workflow with user selection and runtime options.
 * Uses the shared WorkflowTargetSelector component for user selection.
 */

import React, { useState, useCallback, useEffect } from 'react';
import { toast } from 'sonner';
import { X, Play } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { WorkflowTargetSelector } from '@/components/common/WorkflowTargetSelector';
import { UserFilterOptionsForm } from '@/components/common/UserFilterOptionsForm';
import { RuntimeOptionsForm } from './RuntimeOptionsForm';
import { ExecutionAdvancedOptions } from '@/components/common/ExecutionAdvancedOptions';
import { useExecuteWorkflow } from '../hooks/useImageWorkflows';
import { useAuthStore } from '@/stores/authStore';
import { useI18n } from '@/i18n';
import type { ExecuteWorkflowRequest, RuntimeOptions, UserFilterOptions } from '@/types/imageWorkflow';
import type { WorkflowTarget } from '@/types/users';

interface RunWorkflowDialogProps {
  isOpen: boolean;
  workflowId: string;
  workflowName: string;
  definitionRuntimeOptions?: RuntimeOptions;
  onClose: () => void;
  onSuccess?: (
    executionId: string,
    metadata: { totalUsers: number; runtimeOptions: RuntimeOptions },
  ) => void;
}

const DEFAULT_RUNTIME_OPTIONS: RuntimeOptions = {
  disable_memory_tool: true,
  disable_memory_write: true,
  per_user_timeout_seconds: 900,
  max_attempts: 1,
  max_concurrency: 1,
};

export const RunWorkflowDialog: React.FC<RunWorkflowDialogProps> = ({
  isOpen,
  workflowId,
  workflowName,
  definitionRuntimeOptions,
  onClose,
  onSuccess,
}) => {
  const { t } = useI18n();
  // Auth store for current user
  const user = useAuthStore((state) => state.user);

  // Form state - Target users (using WorkflowTargetSelector)
  const [target, setTarget] = useState<WorkflowTarget>({
    type: 'current_user',
    user_ids: [],
  });

  // User filter options (only for all_valid_users)
  const [userFilter, setUserFilter] = useState<UserFilterOptions>({
    exclude_test: true,
    has_self_birth_chart: false,
  });

  const defaultRuntimeOptions = definitionRuntimeOptions ?? DEFAULT_RUNTIME_OPTIONS;
  const [runtimeOptions, setRuntimeOptions] = useState<RuntimeOptions>(defaultRuntimeOptions);
  const [overrideRuntimeOptions, setOverrideRuntimeOptions] = useState(false);

  // API
  const executeWorkflow = useExecuteWorkflow();

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setOverrideRuntimeOptions(false);
      setRuntimeOptions(defaultRuntimeOptions);
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, definitionRuntimeOptions]);

  // Handle execute
  const handleExecute = useCallback(async () => {
    // Validate specific users selection
    if (target.type === 'specific_users' && target.user_ids.length === 0) {
      toast.error(t('imageWorkflows.runDialog.selectAtLeastOneUser'));
      return;
    }

    const request: ExecuteWorkflowRequest = {
      workflow_id: workflowId,
      target_type: target.type,
      target_user_ids: target.user_ids,
      // Only include user_filter when target_type is 'all_valid_users'
      user_filter: target.type === 'all_valid_users' ? userFilter : undefined,
      runtime_options: overrideRuntimeOptions ? runtimeOptions : undefined,
    };

    try {
      const result = await executeWorkflow.mutateAsync(request);
      const userCountText = target.type === 'all_valid_users'
        ? t('imageWorkflows.runDialog.allValidUsers')
        : t('imageWorkflows.runDialog.usersCount').replace('{count}', String(result.total_users));
      // Short-duration "started" toast — terminal toast (success/failed/partial)
      // fires from WorkflowEditor onSuccess once polling settles; default 4s would
      // overlap and look contradictory (e.g. "started" + "failed" both visible).
      toast.success(
        `${t('imageWorkflows.runDialog.workflowStarted')}: ${userCountText}`,
        { duration: 1500 },
      );
      onSuccess?.(result.execution_id, {
        totalUsers: result.total_users,
        runtimeOptions,
      });
      onClose();
    } catch (error) {
      console.error('Execute failed:', error);
      toast.error(t('imageWorkflows.runDialog.executeFailed'));
    }
  }, [
    workflowId,
    target,
    userFilter,
    runtimeOptions,
    overrideRuntimeOptions,
    executeWorkflow,
    onSuccess,
    onClose,
    t,
  ]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Dialog */}
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{t('imageWorkflows.runDialog.title')}</h2>
            <p className="text-sm text-gray-500 mt-1">
              {t('imageWorkflows.runDialog.description')} "{workflowName}"
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="p-4 overflow-y-auto flex-1 space-y-6">
          {/* User Selection */}
          <div className="space-y-4">
            <label className="block text-sm font-medium text-gray-700">
              {t('imageWorkflows.runDialog.targetUsers')}
            </label>
            <WorkflowTargetSelector
              value={target}
              onChange={setTarget}
              currentUserId={user?.user_id}
            />
          </div>

          {/* User Filter Options (only shown when all_valid_users is selected) */}
          {target.type === 'all_valid_users' && (
            <UserFilterOptionsForm
              value={userFilter}
              onChange={setUserFilter}
            />
          )}

          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={overrideRuntimeOptions}
              onChange={(event) => setOverrideRuntimeOptions(event.target.checked)}
            />
            {t('imageWorkflows.runDialog.overrideRuntimePolicy')}
          </label>
          {overrideRuntimeOptions && (
            <>
              <RuntimeOptionsForm value={runtimeOptions} onChange={setRuntimeOptions} />
              <ExecutionAdvancedOptions value={runtimeOptions} onChange={setRuntimeOptions} />
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-200">
          <Button variant="secondary" onClick={onClose}>
            {t('imageWorkflows.runDialog.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleExecute}
            disabled={executeWorkflow.isPending}
          >
            {executeWorkflow.isPending ? (
              <>
                <span className="animate-spin mr-1">⟳</span>
                {t('imageWorkflows.runDialog.executing')}
              </>
            ) : (
              <>
                <Play className="w-4 h-4 mr-1" />
                {t('imageWorkflows.runDialog.execute')}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RunWorkflowDialog;
